// Visual badges for App-side metadata used in LeadCard and LeadDetailPanel.
// Sales-focused: KYC verification, commercial plan, App lifecycle stage.
// Payment / arrears information is intentionally NOT shown — this CRM is
// scoped to lead capture & sales closing, not post-sale operations.
import { ShieldCheck, Star, Sparkles, Crown } from 'lucide-react';

const APP_STATUS_META = {
  registered:  { label: 'Registrado',    color: '#64748b' },
  onboarding:  { label: 'Onboarding',    color: '#2563eb' },
  verified:    { label: 'Verificado',    color: '#059669' },
  active:      { label: 'Activo App',    color: '#059669' },
  eligible_65: { label: 'Elegible 65%',  color: '#7c3aed' },
  settled_100: { label: 'Liquidacion',   color: '#10b981' },
  inactive:    { label: 'Inactivo App',  color: '#dc2626' },
};

// Commercial plan names. KOMPA CLUB displays via the dedicated gold card
// styling on the Kanban — we still expose its meta here so the side panel
// can render the brand label and color.
export const PLAN_META = {
  KOMPA_PLUS:  { label: 'KOMPA+',     color: '#2563eb', icon: Star },
  kompa_plus:  { label: 'KOMPA+',     color: '#2563eb', icon: Star },
  ACELERADO:   { label: 'Acelerado',  color: '#d97706', icon: Star },
  acelerado:   { label: 'Acelerado',  color: '#d97706', icon: Star },
  KOMPA_CLUB:  { label: 'KOMPA CLUB', color: '#b45309', icon: Crown },
  kompa_club:  { label: 'KOMPA CLUB', color: '#b45309', icon: Crown },
  KOMPACLUB:   { label: 'KOMPA CLUB', color: '#b45309', icon: Crown },
  kompaclub:   { label: 'KOMPA CLUB', color: '#b45309', icon: Crown },
};

function Badge({ children, color, icon: Icon, testId, size = 'sm' }) {
  const padding = size === 'sm' ? '2px 6px' : '3px 8px';
  const fontSize = size === 'sm' ? 11 : 12;
  return (
    <span
      data-testid={testId}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding, borderRadius: 10, fontSize, fontWeight: 600,
        background: `${color}22`, color, border: `1px solid ${color}55`,
        whiteSpace: 'nowrap',
      }}
    >
      {Icon && <Icon size={11} />}
      {children}
    </span>
  );
}

export default function LeadBadges({ lead, size = 'sm' }) {
  if (!lead) return null;
  const badges = [];

  if (lead.kyc_completed) {
    badges.push(
      <Badge key="kyc" color="#059669" icon={ShieldCheck} size={size} testId={`badge-kyc-${lead.id}`}>
        KYC
      </Badge>
    );
  }

  const plan = PLAN_META[lead.plan_code];
  if (plan) {
    badges.push(
      <Badge key="plan" color={plan.color} icon={plan.icon} size={size} testId={`badge-plan-${lead.id}`}>
        {plan.label}
      </Badge>
    );
  }

  const appMeta = APP_STATUS_META[lead.app_status];
  if (appMeta) {
    badges.push(
      <Badge key="app" color={appMeta.color} icon={Sparkles} size={size} testId={`badge-app-${lead.id}`}>
        {appMeta.label}
      </Badge>
    );
  }

  if (badges.length === 0) return null;

  return (
    <div
      data-testid={`lead-badges-${lead.id}`}
      style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}
    >
      {badges}
    </div>
  );
}
